import { useContext, useEffect, useState } from 'react';

import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPModal,
  IPPrimaryButton,
  IPSeparator,
  IPSpinner,
  IPText,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import InfoMessage from 'components/Notification/InfoMessage';
import LibraryPicker from 'components/Pickers/LibraryPicker';
import {
  cancelIcon,
  globalStackStylesPanel,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
} from 'globalStyles';
import ResourceLink, { ILinkRow } from 'models/resourceLink';
import ResourceList from 'models/resourceList';
import { useTranslation } from 'react-i18next';
import { apiGetLinksForList } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { IPSpinnerSize } from "baseComponents/Spinner/IPSpinner";

interface IVirtualContentPickerProps {
  list: ResourceList;
  isOpen: boolean;
  existingLinks: ResourceLink[];
  onSelect: (links: ILinkRow[]) => void;
  onClose: () => void;
}

const VirtualContentPicker = (props: IVirtualContentPickerProps) => {
  const { t } = useTranslation(['library', 'translation']);
  const appContext = useContext(AppContext);
  const [selectedLinks, setSelectedLinks] = useState<ResourceLink[]>([]);
  const [links, setLinks] = useState<ResourceLink[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      if (isLoading) return;

      setIsLoading(true);

      await appContext.globalDataCache.lists.getItems(); //make sure the lists are loaded
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const links = await apiGetLinksForList(props.list.listId, accessToken, appContext.globalDataCache);

      setLinks(links);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onSelect = () => {
    const selectedLinkRows = selectedLinks.map((item: ResourceLink) => {
      return {
        item: item,
        isBusy: false,
      };
    });
    props.onSelect(selectedLinkRows);
    setLinks([...links]);
  };

  const onCancel = () => {
    props.onClose();
  };

  return (
    <IPModal isBlocking={true} isOpen={props.isOpen} onDismiss={onCancel}>
      <IPStack
        verticalFill
        styles={{ root: { height: '90vh', width: 600, minWidth: 320, maxWidth: '90vw', padding: 20 } }}
      >
        {/*Header*/}
        <IPStack.Item>
          <IPStack horizontal horizontalAlign="space-between">
            <IPStack.Item>
              <IPText variant="xLarge">{t('library:ApprovalTasks.AddContent.Label')}</IPText>
            </IPStack.Item>
            <IPStack.Item>
              <IPIconButton iconProps={cancelIcon} onClick={onCancel} />
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPSeparator />
        <IPStack.Item>
          <InfoMessage message={t('library:VirtualPicker.Info')} />
        </IPStack.Item>
        {/*Body */}
        <IPStack.Item grow>
          {isLoading ? (
            <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
              <IPSpinner size={IPSpinnerSize.large} />
            </IPStack>
          ) : (
            <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapMedium}>
              <LibraryPicker
                multiSelect={true}
                onSelect={setSelectedLinks}
                links={links}
                lists={appContext.globalDataCache.lists.items}
                isLoading={isLoading}
                expandGroups={true}
                existingLinks={props.existingLinks}
              />
              <IPStack.Item>
                <IPText>
                  {t('library:ApprovalTasks.AddContent.SelectedCount', {
                    total: links.length,
                    selected: selectedLinks.length,
                  })}
                </IPText>
              </IPStack.Item>
            </IPStack>
          )}
        </IPStack.Item>

        {/*Footer*/}
        <IPStack.Item>
          <IPStack horizontal styles={globalStackStylesPanel} tokens={globalStackTokensGapSmall} horizontalAlign="end">
            <IPStack.Item>
              <IPPrimaryButton onClick={() => onSelect()} disabled={selectedLinks.length === 0}>
                {t('translation:General.Button.Add')}
              </IPPrimaryButton>
            </IPStack.Item>
            <IPStack.Item>
              <IPDefaultButton onClick={props.onClose} disabled={isLoading}>
                {t('translation:General.Button.Cancel')}
              </IPDefaultButton>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
      </IPStack>
    </IPModal>
  );
};

export default VirtualContentPicker;
